// supabase/functions/_shared/email/emailImages.ts
// Images in email bodies, signatures and templates (logo in a signature). Shared by the SPA (DOMPurify
// sanitiser, TipTap image node, upload) and the server (structural allowlist, Graph 1:1 sanitiser).
// Pure: no Deno/Vite env here — callers that know their Supabase URL pass extra prefixes.
//
// Only absolute https URLs from our own hosts are ever kept: the CRM's static /email/ assets and the
// public `email-assets` Storage bucket. No data: URIs (Outlook/Gmail strip them), no other hosts
// (tracking pixels), no SVG (not rendered by Outlook/Gmail; a public SVG can carry script).

export const EMAIL_ASSETS_BUCKET = "email-assets";
export const GAVION_LOGO_URL = "https://crm.gavion.ai/email/gavion-logo.png";
const CRM_PREFIX = "https://crm.gavion.ai/";
/** Production Supabase project: the server allowlist cannot read the SPA's VITE_SUPABASE_URL. */
export const PROD_SUPABASE_URL = "https://urxsykxxninpuwgljzfe.supabase.co";

/** Public URL prefix of the email-assets bucket for a Supabase URL; null unless https. */
export function emailAssetsPublicPrefix(
  supabaseUrl: string | null | undefined,
): string | null {
  const base = (supabaseUrl ?? "").trim().replace(/\/+$/, "");
  if (!/^https:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?$/.test(base)) return null;
  return `${base}/storage/v1/object/public/${EMAIL_ASSETS_BUCKET}/`;
}

export const DEFAULT_IMAGE_PREFIXES: readonly string[] = [
  CRM_PREFIX,
  emailAssetsPublicPrefix(PROD_SUPABASE_URL) as string,
];

/** Default prefixes plus the email-assets prefix of `supabaseUrl` (when https). */
export function imagePrefixesFor(
  supabaseUrl: string | null | undefined,
): readonly string[] {
  const extra = emailAssetsPublicPrefix(supabaseUrl);
  return extra && !DEFAULT_IMAGE_PREFIXES.includes(extra)
    ? [...DEFAULT_IMAGE_PREFIXES, extra]
    : DEFAULT_IMAGE_PREFIXES;
}

// After the prefix: plain path segments and a raster extension. No "..", "%", "?", "#", "\", "@".
const ASSET_PATH = /^[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*\.(?:png|jpe?g|gif)$/i;

/** True when `src` is exactly an allowed prefix followed by a safe raster-image path. */
export function isAllowedEmailImageSrc(
  src: unknown,
  prefixes: readonly string[] = DEFAULT_IMAGE_PREFIXES,
): boolean {
  if (typeof src !== "string") return false;
  return prefixes.some(
    (p) => src.startsWith(p) && ASSET_PATH.test(src.slice(p.length)),
  );
}

export const MAX_IMAGE_DIMENSION = 1200;
/** width / height attribute: a plain pixel count, 1..1200. */
export function isValidImageDimension(v: unknown): boolean {
  if (typeof v !== "string" || !/^[0-9]{1,4}$/.test(v)) return false;
  const n = Number(v);
  return n >= 1 && n <= MAX_IMAGE_DIMENSION;
}

const STYLE_PROPS = new Set(["width", "height", "max-width"]);
const STYLE_VALUE = /^(?:[0-9]{1,4}px|[0-9]{1,3}%|auto)$/;

/**
 * Canonical form of an img style, the browser's own cssText serialisation so the editor's HTML and
 * the sanitised HTML are identical ("width: 160px; max-width: 100%; height: auto;"): only width, height and
 * max-width with px, % or auto. Null when any declaration is something else (or nothing is left).
 */
export function normalizeImageStyle(style: unknown): string | null {
  if (typeof style !== "string") return null;
  const out: string[] = [];
  for (const raw of style.split(";")) {
    if (raw.trim() === "") continue;
    const m = /^\s*([a-zA-Z-]+)\s*:\s*([^;]*?)\s*$/.exec(raw);
    if (!m) return null;
    const prop = m[1].toLowerCase();
    const value = m[2].toLowerCase();
    if (!STYLE_PROPS.has(prop) || !STYLE_VALUE.test(value)) return null;
    out.push(`${prop}: ${value};`);
  }
  return out.length ? out.join(" ") : null;
}

/** Pixel width declared in a (normalised) style, else null. */
export function styleWidthPx(style: string | null): number | null {
  const m = /(?:^|;)\s*width:\s*([0-9]{1,4})px/.exec(style ?? "");
  return m ? Number(m[1]) : null;
}

export type ImageSize = "small" | "medium" | "large";
export const IMAGE_SIZES: Record<ImageSize, number> = {
  small: 100,
  medium: 160,
  large: 240,
};
export const DEFAULT_IMAGE_WIDTH = IMAGE_SIZES.medium;

/** Inline style written with every image: Outlook reads the width attribute, the rest scale down. */
export const imageStyle = (width: number): string =>
  `width: ${width}px; max-width: 100%; height: auto;`;
